import { makeCode } from './compute';
import type { Shape } from './compute';
import type { Chat } from './plan';
import { objectParticle } from './korean';
import { PLAIN_WRITING } from './writing';
import { monthKey, statusColumn, statusName } from './reportData';
import type { AgentScreen } from './screenIndex';

/**
 * **업무 분야 전문가.** 분야마다 용어·코드 뜻·분석 틀을 가진다(`kb/experts.json`, 설계
 * `docs/superpowers/specs/2026-10-22-expert-agents-design.md`).
 *
 * 「이번달 중대결함 현장들의 특징을 정리해줘」(실제 테넌트, 2026-10-22). 한 판은 「중대결함」을 중대고장으로 읽어
 * 결재상태 `101` 을 특징이라 했고, 맞는 화면에 간 판도 유형·현장 건수만 셌다. 모자란 것은 일반 추론이 아니라
 * 그 일을 하는 사람이 아는 것이다 — 중대결함은 중대고장이 아니고, B 는 주의관찰이며, 특징을 물으면 쏠림·처리를 본다.
 */
export type ExpertTerm = {
  term: string;
  aliases?: string[];
  /** 그 말이 이 회사 ERP 에서 뜻하는 것. */
  means: string;
  /** 원본 화면. */
  source?: { route: string; screen: string; table?: string };
  /** 헷갈리는 다른 말. */
  notSame?: { term: string; why: string }[];
};

export type ExpertAxis = {
  name: string;
  /** 이 갈래에서 볼 것. */
  brief: string;
  columns: string[];
};

export type ExpertFrame = {
  id: string;
  name: string;
  route: string;
  rowsStateKey: string;
  /** 분석을 청하는 말(정규식 원문). */
  asks: string;
  axes: ExpertAxis[];
  /** 작성 담당이 꼭 짚을 것 — 이 분야 사람이 보는 눈. */
  notes?: string[];
  /** 이 화면·표의 자료 읽기(칸 id). 모델에게 묻지 않고 쓴다(`reading.ts`). */
  reading?: Omit<import('./reading').DataReading, 'names' | 'source'>;
};

export type Expert = {
  id: string;
  name: string;
  /** 화면 `module`. */
  modules: string[];
  /** capability 앞머리(`maintenance`). */
  capabilityPrefixes: string[];
  terms: ExpertTerm[];
  /** 화면 → 표 → 칸 → 값 → 뜻. */
  codes?: Record<string, Record<string, Record<string, Record<string, string>>>>;
  frames?: ExpertFrame[];
};

/** 틀마다 갈래는 이만큼까지 — 하루 AI 한도가 있다. */
export const MAX_AXES = 6;

/**
 * 전문가의 코드 뜻을 칸에 채운다. 계산은 칸의 `codes` 를 「코드값 B=주의관찰」로 받는다
 * (`columnMeaning`) — 5,928칸 중 164칸만 뜻이 있어 결재상태 `101` 이 답까지 그대로 갔다.
 * 카탈로그의 뜻보다 앞선다 — 뽑은 뜻에 색 값(`var(--color-blue)`)이 들어간 칸이 있었고, 전문가 지식은 앱 소스로
 * 확인해 쓴 것이다(2026-10-22). 원본은 바꾸지 않는다.
 */
export function applyExpertCodes(screens: AgentScreen[], experts: Expert[]): AgentScreen[] {
  const known = (route: string, grid: string, column: string) => {
    for (let index = 0; index < experts.length; index += 1) {
      const meanings = experts[index]?.codes?.[route]?.[grid]?.[column];
      if (meanings) return meanings;
    }
    return undefined;
  };
  return screens.map((screen) => {
    if (!experts.some((expert) => expert.codes?.[screen.route])) return screen;
    return {
      ...screen,
      grids: screen.grids.map((grid) => ({
        ...grid,
        columns: grid.columns.map((column) => {
          const meanings = known(screen.route, grid.key, column.id);
          return meanings
            ? { ...column, codes: Object.keys(meanings).map((value) => ({ value, label: meanings[value] ?? '' })) }
            : column;
        }),
      })),
    };
  });
}

function said(question: string, term: ExpertTerm): boolean {
  return [term.term, ...(term.aliases ?? [])].some((word) => question.includes(word));
}

/** 용어가 아니라 다른 말(오타·은어)로 걸렸으면 그 말. 「시제」는 「시재」를 말한 것이다. */
function saidAs(question: string, term: ExpertTerm): string | undefined {
  if (question.includes(term.term)) return undefined;
  return (term.aliases ?? []).find((word) => question.includes(word) && !word.includes(term.term));
}

/** 용어 화면은 이만큼까지 — 계획 후보(열둘) 자리를 다 차지하지 않게. */
const MAX_TERM_ROUTES = 3;

/**
 * 질문에 걸린 용어의 원본 화면. 계획 후보에 넣는다(`PlanContext.termRoutes`) — 검색은 「시제」·「들어온 돈」 같은
 * 말에서 맞는 화면을 못 찾는다(전수 1,128건 중 19%가 후보 밖, 2026-10-23).
 */
export function termRoutes(
  question: string,
  experts: Expert[],
  screens: AgentScreen[] = [],
  /** 과제 단계가 고른 용어(다른 말로 부른 것). 글자로 걸리지 않아도 그 원본 화면을 넣는다. */
  chosen: ChosenTerm[] = [],
): string[] {
  // 화면 이름 셋 이상에 든 말(매출·현장·공사)은 넓다 — 이름 겹침이 이미 후보를 찾고, 한 화면을 박으면 자리만 먹는다.
  const broad = (word: string) => screens.filter((screen) => screen.name.includes(word)).length >= 3;
  const routes: string[] = [];
  // 고른 용어가 먼저다. 넓은 말(현장, 매출)은 고른 것이어도 넣지 않는다: 그 화면이 계획 후보 첫 자리와 업무 도구 계약 비킴을
  // 차지한다(독립 검토).
  experts.forEach((expert) => expert.terms.forEach((term) => {
    const route = term.source?.route;
    if (route && chosen.some((one) => one.term === term.term) && !broad(term.term) && !routes.includes(route)) routes.push(route);
  }));
  experts.forEach((expert) => expert.terms.forEach((term) => {
    const route = term.source?.route;
    const words = [term.term, ...(term.aliases ?? [])].filter((word) => question.includes(word));
    if (route && words.length && !words.every(broad) && !routes.includes(route)) routes.push(route);
  }));
  return routes.slice(0, MAX_TERM_ROUTES);
}

/** 과제 단계가 고른 용어와 그 구절(`TaskSpec.terms`). */
export type ChosenTerm = { term: string; quote: string };

/** 용어 이름 목록. 과제 단계에 싣는다(뜻은 고른 뒤 `glossaryFor`가 붙인다). */
export function termIndex(experts: Expert[]): string[] {
  return Array.from(new Set(experts.flatMap((expert) => expert.terms.map((term) => term.term))));
}

/**
 * 질문에 든 용어의 뜻과 헷갈리는 말. 과제·계획 단계에 붙인다. 없으면 빈 글. 과제 단계가 고른 용어(`chosen`)는 글자로 안 걸려도
 * 싣고, 사람이 부른 말을 밝힌다(「중대 이슈」라고 했으면 「중대결함」을 말한다).
 */
export function glossaryFor(question: string, experts: Expert[], chosen: ChosenTerm[] = []): string {
  const lines = experts.flatMap((expert) => expert.terms
    .filter((term) => said(question, term) || chosen.some((one) => one.term === term.term)).map((term) => {
      const source = term.source
        ? ` 원본은 ${term.source.screen}(${term.source.route}${term.source.table ? ` ${term.source.table}` : ''}) 화면이다.`
        : '';
      const others = (term.notSame ?? []).map((other) => ` ${other.term}과 다르다 — ${other.why}.`).join('');
      const quoted = chosen.find((one) => one.term === term.term && one.quote && !one.quote.includes(term.term))?.quote;
      const as = saidAs(question, term) ?? (said(question, term) ? undefined : quoted);
      const heard = as ? ` 「${as}」라고 했으면 「${term.term}」${objectParticle(term.term).slice(term.term.length)} 말한다.` : '';
      return `- 「${term.term}」: ${term.means}.${heard}${source}${others}`;
    }));
  return lines.length ? `업무 용어(이 회사 ERP 에서 뜻이 정해진 말 — 그대로 따른다):\n${lines.join('\n')}` : '';
}

/**
 * 분석을 청하고 그 틀의 화면·표를 실제로 읽었으면(행이 있으면) 그 틀. 청했는지는 틀의 `asks` 정규식이나 과제 단계가 검사를 지난
 * 분석 구절(`asked`)로 본다. 「자료조사부터 해줘」, 「중대결함으로 조사해야해」는 `asks`에 걸리지 않았다(실제 테넌트, 2026-10-27).
 */
export function frameFor(
  question: string,
  experts: Expert[],
  read: { route: string; rowsStateKey: string; rows: number }[],
  /** 분석 구절이 검사를 지났을 때 고른 용어의 원본 화면. 그 화면의 틀만 분석 구절로 돈다(「보고서 준비해줘」가 다른 틀까지 돌지 않게). */
  askedRoutes: string[] = [],
): ExpertFrame | undefined {
  for (let index = 0; index < experts.length; index += 1) {
    const frame = (experts[index]?.frames ?? []).find((one) => (askedRoutes.includes(one.route) || new RegExp(one.asks).test(question))
      && read.some((retrieval) => retrieval.route === one.route
        && retrieval.rowsStateKey === one.rowsStateKey && retrieval.rows > 0));
    if (frame) return frame;
  }
  return undefined;
}

export type AxisResult = { axis: string; value?: unknown; failed?: string };

/** 없음·누락을 말하는 열쇠. 그 아래의 0은 「빈 칸이 없다」는 주장이다. */
const ABSENT = /누락|없음|미입력|빈\s*칸|공란|미기재/;
/** 글 속의 달(1~12): 「2026-10」, 「202610」, 「10월」. 없으면 0. */
function monthIn(text: string): number {
  const dated = text.match(/(?:19|20)\d{2}[-./]?(0[1-9]|1[0-2])(?!\d)/);
  if (dated) return Number(dated[1]);
  const named = text.match(/(?<!\d)(1[0-2]|0?[1-9])\s*월/);
  return named ? Number(named[1]) : 0;
}

/** 빈 칸만 말하는 열쇠와 넓게 말하는 열쇠. 「빈 칸 0건」은 빈 칸이 없다는 말뿐이다. */
const BLANK_ONLY = /빈\s*칸|공란/;
const BROAD = /누락|없음|미입력|미기재/;

/**
 * 갈래 결과의 「누락·없음 0건」을 원자료로 검산한다. 끝낸 행에 빈 칸이 있는 칸을 0건으로 셌으면 까닭을 돌려준다.
 * 고객싸인 사전은 「있음」 하나라 안 받은 행이 빈 칸인데, 계산이 다른 표시를 찾아 「서명 누락 0건」을 냈다
 * (2026-10-25 09:14, 같은 칸이 09:03 판에는 1,807건). 「누락 0건」은 「문제없음」으로 읽혀 거짓으로 안심시킨다(11차 검토).
 * 열쇠가 칸 이름을 말하면 그 칸만, 아무 칸도 말하지 않으면(「서명누락_하나이상」) 갈래의 모든 칸을 본다.
 * 빈 칸이 아직 안 한 행에만 있으면 0건이 맞을 수 있어 묻지 않는다. 사전이 있는 칸(「값: 있음」)은 사전 값이 아니면
 * 없음으로 본다: 이 회사는 안 받은 서명을 빈 칸이 아니라 코드 「0」으로 둔다(10:51 판 칸 설명 「그 밖의 값: 0 2,199행」).
 */
export function axisDoubt(value: unknown, axis: ExpertAxis, shapes: Shape[], rows: Record<string, unknown>): string | undefined {
  // 0을 말한 열쇠의 길과, 같은 묶음에 나란히 있는 열쇠들.
  const found: { path: string; last: string; siblings: string[] }[] = [];
  const walk = (node: unknown, path: string[], siblings: string[]): void => {
    if (typeof node === 'number') {
      if (node === 0 && path.some((key) => ABSENT.test(key))) found.push({ path: path.join(' '), last: path[path.length - 1] ?? '', siblings });
    } else if (Array.isArray(node)) node.forEach((one) => walk(one, path, siblings));
    else if (node && typeof node === 'object') {
      const keys = Object.keys(node as Record<string, unknown>);
      Object.entries(node as Record<string, unknown>).forEach(([key, one]) => walk(one, [...path, key], keys.filter((other) => other !== key)));
    }
  };
  walk(value, [], []);
  if (!found.length) return undefined;
  let doubt: string | undefined;
  shapes.some((shape) => {
    const list = Array.isArray(rows[shape.label]) ? rows[shape.label] as Record<string, unknown>[] : [];
    if (!list.length) return false;
    const ids = shape.columns.map((column) => column.id);
    const nameOf = (id: string) => (shape.columns.find((one) => one.id === id)?.text ?? id).split(',')[0]?.trim() || id;
    const labelsOf = (id: string) => (shape.columns.find((one) => one.id === id)?.text ?? '').match(/값:\s*([^,]+)/)?.[1]
      ?.split('·').map((label) => label.trim()).filter(Boolean);
    const status = statusColumn({ columns: ids, rows: list.map((row) => ids.map((id) => row[id])) });
    // 상태 값(완료·계획)과 나란한 「…상태」 0건은 상태 칸의 분포다. 끝낸 뒤 채우는 칸의 빈 칸 주장이 아니다(실측 07:17:
    // 「월별 2026-09 빈칸·기타 상태」를 갈래의 모든 칸에 빈 칸이 없다는 말로 읽어 맞는 계산을 버렸다).
    const states = status ? labelsOf(ids[status.index] ?? '') ?? [] : [];
    // 열쇠에 달이 있으면 그 달 표에만 대조한다(실측 08:49: 「2026-10 완료행_시작시간없음」을 9월 표의 빈 시작시간 8행과 대조했다).
    const shapeMonth = monthIn(shape.label);
    const claims = found.filter((one) => !(/상태/.test(one.last)
      && one.siblings.some((key) => states.some((label) => key.includes(label)))))
      .filter((one) => !shapeMonth || !monthIn(one.path) || monthIn(one.path) === shapeMonth).map((one) => one.path);
    if (!claims.length) return false;
    const done = status ? list.filter((row) => {
      const cell = String(row[ids[status.index] ?? ''] ?? '').trim();
      return !!cell && statusName(status, cell) !== status.attention;
    }) : list;
    // 표의 다른 칸(갈래 밖)을 말한 열쇠는 그 칸 이야기다. 이 갈래 칸과 대조하지 않는다(실측 09:09: 「… 고객싸인 빈 칸」을
    // 점검일·시간 갈래의 시작시간과 대조했다).
    const foreign = (claim: string) => shape.columns.some((column) => axis.columns.indexOf(column.id) < 0
      && nameOf(column.id).length >= 2 && claim.includes(nameOf(column.id)));
    const general = claims.some((claim) => !axis.columns.some((id) => claim.includes(nameOf(id))) && !foreign(claim));
    return axis.columns.some((id) => {
      if (ids.indexOf(id) < 0) return false;
      // 이 칸을 말한 열쇠와 아무 칸도 말하지 않은 열쇠(「서명누락_하나이상」).
      const mine = claims.filter((claim) => claim.includes(nameOf(id))
        || (general && !axis.columns.some((other) => claim.includes(nameOf(other))) && !foreign(claim)));
      if (!mine.length) return false;
      const labels = labelsOf(id);
      // 「빈 칸 0건」은 빈 칸만 대조한다. 갈래 계산에 빈 칸과 사전에 없는 값을 따로 세라고 시키므로, 안 받은 서명이 코드 「0」이면
      // 맞는 말이다(실측 2026-10-26 05:35, 서명 갈래가 두 번 다 걸렸다). 넓게 말한 열쇠가 있으면 사전 값이 아닌 행 전부와 대조한다.
      const broad = mine.find((claim) => !BLANK_ONLY.test(claim) || BROAD.test(claim));
      const outside = Boolean(broad && labels?.length);
      const absent = (cell: unknown) => (outside ? (labels ?? []).indexOf(String(cell ?? '').trim()) < 0 : String(cell ?? '').trim() === '');
      const missing = done.filter((row) => absent(row[id])).length;
      const count = `${missing.toLocaleString('en-US')}행`;
      if (missing > 0) {
        // 어느 열쇠를 대조했는지 붙인다. 다시 짤 때와 기록에서 보인다.
        doubt = `${nameOf(id)} ${outside ? `「${(labels ?? []).join('·')}」이 아닌` : '빈 칸인'} 끝낸 행 ${count}을 0건으로 셌다`
          + `(「${broad ?? mine[0]}」)`;
      }
      return missing > 0;
    });
  });
  return doubt;
}

/** 달마다 전체를 말하는 열쇠. */
const TOTAL_KEY = /^(?:전체|전체\s*건수|점검\s*건수|전체\s*점검\s*건수|총\s*건수)$/;

/**
 * 갈래 값의 달별 전체를 〈표 1〉 달별 전체(months: [YYYYMM, 건수])와 대조한다. 실측 13:06의 「계획 대비 완료」 갈래는
 * 기간(9월 1일~10월 25일)을 점검일자로 잘라 10월 전체를 2,002건(25일까지 예정분)으로 셌고, 1턴 답이 그 10월 「계획이 남은
 * 현장」(경과분)을 「미실시 현장 가운데 가장 많다」로 썼다. 맞지 않는 달이 있으면 까닭을 돌려준다.
 */
export function monthTotalDoubt(value: unknown, months?: [string, number][]): string | undefined {
  if (!months?.length) return undefined;
  const canon = new Map(months.map(([key, total]) => [monthKey(key), total] as const).filter(([key]) => !!key));
  let doubt: string | undefined;
  const check = (month: string, node: unknown) => {
    const expected = canon.get(month);
    if (doubt || expected === undefined || !node || typeof node !== 'object' || Array.isArray(node)) return;
    const total = Object.entries(node as Record<string, unknown>)
      .find(([key, one]) => TOTAL_KEY.test(key.trim()) && typeof one === 'number')?.[1] as number | undefined;
    if (total === undefined || total === expected) return;
    const name = `${Number(month.slice(4))}월`;
    doubt = `${name} 전체를 ${total.toLocaleString('en-US')}건으로 셌다. ${name} 행은 ${expected.toLocaleString('en-US')}건이다. `
      + '기간을 날짜 칸으로 자르지 말고 달 칸과 상태 칸으로 세라';
  };
  const walk = (node: unknown): void => {
    if (doubt) return;
    if (Array.isArray(node)) {
      node.forEach((one) => {
        const field = one && typeof one === 'object' && !Array.isArray(one)
          ? Object.entries(one as Record<string, unknown>).find(([key, cell]) => /^(?:대상)?월$|^달$|^month$/i.test(key)
            && monthKey(String(cell ?? ''))) : undefined;
        if (field) check(monthKey(String(field[1])), one);
        walk(one);
      });
    } else if (node && typeof node === 'object') {
      Object.entries(node as Record<string, unknown>).forEach(([key, one]) => {
        if (monthKey(key)) check(monthKey(key), one);
        walk(one);
      });
    }
  };
  walk(value);
  return doubt;
}

/**
 * **갈래마다 분석 담당이 따로 계산한다.** 한 번의 계산은 질문 전체를 한 코드로 풀려다 건수 세기로 끝났다.
 * 갈래는 자기 칸만 보고 깊게 센다. 계산 짜기(LLM)는 동시에 부르고, 실행은 한 워커라 차례로 한다.
 * 실패한 갈래는 오류를 들고 한 번 다시 짠다. 그래도 안 되면 빼고 까닭을 남긴다 — 문서가 밝힌다.
 */
export async function analyzeAxes(input: {
  question: string;
  frame: ExpertFrame;
  shapes: Shape[];
  rows: Record<string, unknown>;
  totals: Record<string, unknown>;
  chat: Chat;
  sandbox: { run: (code: string, data: { rows: Record<string, unknown>; totals: Record<string, unknown> }) => Promise<unknown> };
  rules?: string;
  /** 〈표 1〉 달별 전체 [YYYYMM, 건수]. 갈래가 달을 날짜로 다시 잘랐는지 대조한다(실측 13:06). */
  months?: [string, number][];
}): Promise<AxisResult[]> {
  const axes = input.frame.axes.slice(0, MAX_AXES);
  const ask = (axis: ExpertAxis, error = '') => makeCode(
    `${input.question}

이 계산은 「${input.frame.name}」 분석의 한 갈래만 맡는다: ${axis.name} — ${axis.brief}.
주로 쓸 칸: ${axis.columns.join(', ')}. 다른 갈래는 다른 계산이 맡으니 이 갈래를 깊게 센다.
결과는 건수만이 아니라 비율(소수 첫째 자리)과 상위 항목 이름을 담아라. 코드값은 뜻으로 바꿔 담아라.
달 칸이 있으면 달별 전체·상태별 건수는 달 칸과 상태 칸으로 센다. 날짜 칸으로 다시 자르지 않는다(날짜 칸은 기한이 지났는지
나눌 때만 쓴다). 달 칸에 달이 둘이나 셋이면 모든 수(전체·상태별 건수, 순위, 비율)를 달마다 따로 담는다.
여러 달을 더한 기간 합계는 담지 않는다. 빈 칸이나 사전에 없는 값도 따로 세어 담는다. 상태 칸이 있으면 끝낸 뒤에 채우는
칸(서명·처리자·완료 시간·정보센터 전송)의 빈 값과 미전송은 끝낸 행에서만 센다(아직 안 한 행은 비어 있는 것이 맞다).${error
  ? `\n\n직전 코드가 이렇게 멈췄다 — 고쳐서 다시 짜라: ${error}` : ''}`,
    input.shapes,
    input.chat,
    false,
    undefined,
    input.rules ?? '',
  );
  const codes = await Promise.all(axes.map((axis) => ask(axis).catch((error) => error as Error)));
  const results: AxisResult[] = [];
  for (let index = 0; index < axes.length; index += 1) {
    const axis = axes[index] as ExpertAxis;
    let code = codes[index];
    let failed = '';
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        if (code instanceof Error) throw code;
        // eslint-disable-next-line no-await-in-loop
        const value = await input.sandbox.run(code as string, { rows: input.rows, totals: input.totals });
        // 계산이 result에 담지 않으면 값이 빈다(파이썬 None). 「됨」으로 넣었더니 작성 담당과 다음 턴 보고서가 그 값을 글로
        // 옮기다 멈췄다(실측 2026-10-26 05:35). 오류로 보고 한 번 다시 짜게 한다.
        if (value === undefined || value === null) throw new Error('계산이 결과를 내지 않았다. 결과를 전역 변수 result에 담아라');
        // 「누락 0건」을 원자료로 검산한다. 맞지 않으면 까닭을 들려 한 번 다시 짜게 하고, 그래도 맞지 않으면 뺀다(11차 검토).
        const doubt = axisDoubt(value, axis, input.shapes, input.rows);
        if (doubt) throw new Error(`검산이 맞지 않는다: ${doubt}. 그 행도 없음으로 세라`);
        // 달별 전체도 〈표 1〉과 대조한다. 맞지 않으면 같은 길로 한 번 다시 짜고, 그래도 다르면 뺀다(실측 13:06).
        const off = monthTotalDoubt(value, input.months);
        if (off) throw new Error(`검산이 맞지 않는다: ${off}`);
        results.push({ axis: axis.name, value });
        failed = '';
        break;
      } catch (error) {
        failed = String((error as Error)?.message ?? error).slice(0, 300);
        if (attempt === 0) {
          // eslint-disable-next-line no-await-in-loop
          code = await ask(axis, failed).catch((again) => again as Error);
        }
      }
    }
    if (failed) {
      // 왜 못 했는지 남긴다 — 세 달 7,017행 판에서 여섯 갈래 중 다섯이 실패했는데 까닭이 기록에 없었다(2026-10-25 08:29).
      // eslint-disable-next-line no-console
      console.debug('[agent] 갈래 실패', axis.name, failed.slice(0, 200));
      results.push({ axis: axis.name, failed });
    }
  }
  return results;
}

// 진행 중인 달 규칙 — 10월 25일 현재 83.1%를 9월 월말 97.8%와 견줘 「이번 달 완료율이 낮습니다」라고 썼다(8차 검토, 2026-10-25).
// 경과·빈 칸 규칙 — 첫 답이 미전송·서명을 앞세워 보고서(경과 52건)와 결론이 달랐고, 미실시라 비어 있는 처리자 396건을
// 「책임 소재」 결함으로, 완료 건의 93%에 없는 고객싸인을 「누락」으로 단정했다(9차 검토, 2026-10-25).
// 달별 몫 규칙 — 「'요새'는 10월로 보았다」면서 111건에 9월분 51건을 넣었고, 9~10월 미전송과 10월 서명을 한 문장에
// 섞었다(10차 검토, 2026-10-25).
const WRITER_PROMPT = `너는 이 회사 {expert} 다. 동료가 물었다: {question}

갈래별 분석 담당이 같은 자료를 나눠 계산한 결과가 아래에 있다. 이것으로 사무직 동료가 그대로 읽고
보고에 쓸 수 있는 문서를 마크다운으로 쓴다.

절:
## 요약 — 핵심 세 줄 안팎. 가장 눈에 띄는 특징부터.
## 갈래별로 본 것 — 갈래마다 ### 소제목, 숫자와 상위 이름을 들어 무엇이 특징인지 해석한다.
## 눈여겨볼 점 — 갈래를 엮어 보이는 것(쏠림·미처리·반복 등)과 그에 맞는 권하는 조치.
## 기준과 한계 — 기간·조건, 계산하지 못한 갈래, 자료로 알 수 없는 것.

규칙:
- 숫자는 아래 결과에 있는 것만 쓴다. 결과에 없는 비율·합계를 새로 셈하지 마라.
- 코드값(B, 0, 101 등)을 그대로 쓰지 말고 뜻으로 쓴다.
- 칸 이름·영문 키·화면 경로를 쓰지 마라. 사람 말로 쓴다.
- 계산하지 못한 갈래는 지어내지 말고 「기준과 한계」에 「○○ 집계를 확인하지 못해 이번 답에서 뺐습니다」 한 줄로만 적는다.
  못 한 까닭 속 숫자는 옮기지 않는다.
- 오늘이 속한 달은 아직 진행 중이다. 마감된 달(지난달 월말 확정치)과 그대로 견주어 낮다·높다고 쓰지 마라 —
  견주려면 「○일 현재」라고 밝힌다.
- 「미실시」는 결과가 계획으로 남은 건 전체다(아래 사실의 달별 줄과 같은 말). 아래 사실에 예정일 경과가 있으면 그것이
  지연(미실시 가운데 예정일이 지난 것) 판정이다. 「계획·완료 수치만으로 미완료를 단정할 수 없다」고 쓰지 말고, 요약에서 경과
  건수와 쏠림(담당·현장)을 먼저 말한다. 예정일이 남은 건은 「미실시로 확정하지 않았다」고 쓰지 말고 「예정일이 남은 N건(당일
  M건 포함)은 지연으로 보지 않았다」처럼 쓴다. N은 당일을 넣은 수(아래 사실의 경과 외 미실시 전체)다.
- 진행 중인 달의 미실시를 현장별·담당별로 들 때는 「예정 포함」을 밝히거나, 예정일이 지난 건의 현장·담당으로 든다. 예정일이
  안 온 건이 섞인 수로는 문제의 크기가 보이지 않는다.
- 두 달 모두 미실시 호기의 기준월분은 기준월 예정일 경과 건수에 들어 있다. 「N호기에 더해 경과 M건」처럼 더해 읽히게 쓰지 말고
  「경과 M건 중 N건은 두 달 모두 미실시 호기, 나머지는 기준월에만 지난 호기」처럼 쓴다.
- 담당자를 탓하는 말(「… 상태로 두고 있다」)을 쓰지 않는다. 「○○ 담당 배정 N건은 모두 미실시다」처럼 사실로 쓴다. 원인을
  모르는 처방(인력 배정 등)을 쓰지 말고, 조치는 미실시 사유 확인부터 쓴다.
- 「기준과 한계」에 쓰는 기준(마감치와 현재치를 견주지 않는다 등)은 「눈여겨볼 점」에 다시 쓰지 않는다.
- 아직 하지 않은 건(미실시·계획)에 비어 있는 칸(처리자·완료일·서명 등)은 결함이 아니다 — 문제로 쓰지 마라.
- 칸이 대부분 비어 있으면 누락으로 단정하지 마라 — 「○○ 없음 N건, 필수 여부 확인 필요」로 쓴다.
- 여러 달을 합친 수는 달별 몫을 밝힌다(「111건(9월분 51건 포함)」). 기간이 다른 숫자는 한 문장에 섞지 마라 —
  기간이 다르면 문장을 나눈다. 아래 사실에 「두 달 모두 미실시」 묶음이 있으면 요약과 눈여겨볼 점에서 그것을 먼저
  말한다(보고서 첫 줄과 같은 차례).
- 같은 호기가 여러 달에 세어지면 「111건(73호기)」처럼 건과 호기를 함께 쓴다. 「반복된 호기 111건」처럼 섞지 않는다.
- 순위를 들 때 끝이 동점이면 동점인 이름을 모두 쓰거나 「각 2건인 현장 4곳」처럼 쓴다. 동점 가운데 일부만 골라 쓰지 않는다.
- 정보센터 미전송은 끝낸 건 중에서만 말한다. 아직 안 한 점검은 전송할 것이 없다. 아래 사실에 「완료 후 미전송」이 있으면
  그 수를 쓰고, 미전송 전체를 전송 누락처럼 쓰지 않는다. 나머지는 「아직 실시하지 않아 전송할 것이 없다」로 쓴다(「전송 대상이
  아니다」로 쓰지 않는다). 현장 이름은 원자료 그대로 쓴다.
- 완료 후 미전송과 「전송중」에서 바뀌지 않은 건은 할 일이 다르다. 미전송은 전송 처리, 「전송중」은 정보센터 반영 확인이다.
  한 문장에 한 조치로 묶지 말고, 「전송중」이라는 말과 아래 사실의 담당별 수를 그대로 쓴다.
- 아래 사실에 완료 점검 메모(비고)의 이상 기재가 있으면 요약과 눈여겨볼 점에 현장과 내용을 쓴다. 판정 항목이 아니라
  메모 문구라고 밝히고, 메모가 붙은 행 수를 이상 건수로 쓰지 않는다. 「판단할 수 없다」고만 쓰지 않는다.
- 전체·완료·미실시 건수는 아래 사실의 달별 줄(「'26.10월(25일 현재): 전체 …」, 한 달이면 「합계: …」) 숫자로 쓴다.
  갈래 결과가 날짜 칸으로 잘라 센 건수처럼 기준이 다른 수는 「완료」「계획」이라는 이름으로 쓰지 않는다.
  쓰려면 「예정일 지난 미실시 60건」처럼 기준을 밝힌 이름으로 쓴다.
- 같은 달의 수가 갈래마다 다르면 아래 사실에서 까닭(날짜 오류 등)을 찾아 쓴다. 까닭을 모르면 그 수를 빼고,
  「집계 기준 차이」로 덮지 않는다.
- 무엇을 대조했는지, 합산하지 않았는지 같은 우리 쪽 작업은 쓰지 않는다.
{notes}
${PLAIN_WRITING}

반드시 반영할 사실:
{facts}

갈래별 결과:
{results}`;

function resultText(results: AxisResult[]): string {
  return results.map((one) => (one.failed
    ? `### ${one.axis}\n계산하지 못했다(${one.failed.slice(0, 120)})`
    : `### ${one.axis}\n${JSON.stringify(one.value ?? null).slice(0, 6000)}`)).join('\n\n');
}

/** **작성 담당.** 갈래 결과를 모아 절이 있는 문서를 쓴다. 결과에 없는 숫자를 쓰면 한 번 고치게 한다. */
export async function writeReport(input: {
  question: string;
  frame: ExpertFrame;
  expertName?: string;
  results: AxisResult[];
  facts: string[];
  /** 코드가 쓴 절. 요약 바로 아래에 그대로 싣고, 작성 담당에게는 그 묶음을 다시 풀어 쓰지 말라고 한다(13차 검토). */
  fixed?: { title: string; lines: string[] };
  /** 달별 현장 미실시·경과 수와 모든 현장 이름. 미실시·경과 문장의 「현장 N건」을 대조한다(실측 15:20). */
  sites?: SiteCounts;
  /** 현장별 메모 원문. 메모 예시의 장치 낱말이 그 현장 메모에 있는지 대조한다(17차 검토). */
  memos?: { site: string; memo: string }[];
  /** 기준월 예정일 경과 전체의 범위. 「예정일이 지난 … 예정일은 X~Y」를 대조한다(17차 검토). */
  overdue?: { month: string; first: number; last: number };
  chat: Chat;
}): Promise<string> {
  const fixed = input.fixed?.lines.length ? input.fixed : undefined;
  // 계산용 과제 정리 줄(구분·처리·산출물·문서)은 작성 담당에게 주지 않는다. 「기준과 한계」에 「구분은 현장, 처리는 미실시 점검
  // 확인입니다」로 옮겨졌다(실측 14:47). 기간이 든 「조건」 줄은 남긴다.
  const asked = input.question.split('\n')
    .filter((line) => !/^(?:구분|처리|산출물(?:\s*필수\s*열)?|문서)\s*:/.test(line.trim())).join('\n');
  const prompt = WRITER_PROMPT
    .replace('{expert}', () => input.expertName ?? '업무 담당자')
    .replace('{question}', () => asked)
    .replace('{notes}', () => (input.frame.notes?.length
      ? `- 이 분야에서 꼭 짚을 것: ${input.frame.notes.join(' / ')}\n` : '') + (fixed ? fixedRule(fixed) : ''))
    .replace('{facts}', () => (input.facts.length ? input.facts.map((one) => `- ${one}`).join('\n') : '- 없음'))
    .replace('{results}', () => resultText(input.results))
    // 다 됐는데 「일부 갈래는 결과가 없었다」고 지어냈다(실제 테넌트, 2026-10-22). 몇 개가 됐는지 런타임이 말한다.
    + (input.results.some((one) => one.failed)
      ? `\n\n계산하지 못한 갈래: ${input.results.filter((one) => one.failed).map((one) => one.axis).join(', ')}.`
      : `\n\n갈래 ${input.results.length}개를 모두 계산했다. 계산하지 못한 갈래가 있다고 쓰지 마라.`);
  // 코드가 쓴 절은 숫자 대조를 마친 뒤에 넣는다(그 숫자는 근거에서 왔다).
  // 반드시 반영할 사실도 근거다. 코드가 센 값(「9·10월 모두 미실시 38호기」)을 결과에 없다며 고쳐 쓰게 했다.
  const known = [...input.results, { axis: '사실', value: input.facts }];
  const merged = mergedTotals(input.results, input.facts);
  const counts = memoCounts(input.facts);
  const sends = sendCounts(input.facts);
  // 같은 검사를 처음 글과 다시 쓴 글에 돌린다(18차 검토: 다시 쓴 글에는 검사가 다시 돌지 않았다).
  const review = (doc: string) => {
    const stray = unsupportedNumbers(doc, known);
    // 달별 값을 더한 두 달 합계를 달별 몫 없이 썼다(실측 12:46: 계획 447건, 완료 4,227건).
    const mixed = mixedNumbers(doc, merged);
    // 메모 문장의 「N개 현장」은 근거의 그 달 메모 현장 수와 맞아야 한다(15차 검토: 옛 수 「23개 현장」이 남을 수 있다).
    const memoWrong = memoMismatch(doc, counts);
    // 미전송 문장이 아직 안 한 점검까지 센 수를 완료 건 말 없이 쓰면 고치게 한다(16차 검토).
    const sendWrong = sendMismatch(doc, sends);
    // 미실시·경과 문장의 「현장 N건」·「담당 N건」은 그 달 수와 맞아야 한다(실측 15:20, 16:16). 순위는 위에서부터 빠뜨리지
    // 않고, 미실시 순위에 예정일이 안 온 건이 섞였으면 밝힌다(18차 검토).
    const siteWrong = siteMismatch(doc, input.sites);
    const countWrong = siteWrong.filter((one) => one.kind !== 'upcoming');
    const upcoming = siteWrong.filter((one) => one.kind === 'upcoming');
    // 메모 예시의 현장과 장치 낱말, 경과 전체의 예정일 범위도 대조한다(17차 검토).
    const exampleWrong = memoExampleMismatch(doc, input.memos);
    const rangeWrong = rangeMismatch(doc, input.overdue);
    // 미전송과 「전송중」을 한 조치로 묶은 문장, 「미실시로 확정하지 않았다」(19차 검토).
    const together = sendTogether(doc);
    const undue = undueWrong(doc);
    // 「예정일이 남은 N건(당일 M건 포함)」의 N, 미전송 나머지를 「미실시 N건」으로 쓴 문장(실측 07:33).
    const within = withinMismatch(doc, input.facts);
    const mislabeled = remainderMislabel(doc, sends, input.facts);
    const [example] = mixed;
    const asks = [
      stray.length ? `직전 문서의 숫자 ${stray.join(', ')} 는 결과에 없다. 그 숫자를 빼거나 결과에 있는 숫자로 고쳐라.` : '',
      example ? `직전 문서의 숫자 ${unique(mixed.map((one) => one.text)).join(', ')} 는 여러 달을 더한 수다. 달별로 나눠 쓰거나, `
        + `그 줄에 달별 몫을 밝혀라(「${example.sum}건(${example.month}분 ${example.part}건 포함)」).` : '',
      memoWrong.length ? `직전 문서의 메모 현장 수 ${unique(memoWrong.map((one) => one.text)).join(', ')} 는 근거와 다르다(근거: `
        + `${Array.from(counts.entries()).map(([month, count]) => `${month ? `${month} ` : ''}${count}개 현장`).join(', ')}). 근거의 수로 고쳐라.` : '',
      exampleWrong.length ? `직전 문서의 메모 예시 ${exampleWrong.map((one) => `${one.site}의 「${one.claim}」`).join(', ')} 는 그 현장 메모와 `
        + `다르다(${exampleWrong.map((one) => `${one.site} 메모: 「${one.memo}」`).join(' / ')}). 현장과 메모를 맞춰 고쳐라.` : '',
      rangeWrong.length && input.overdue ? `직전 문서의 예정일 범위 ${unique(rangeWrong.map((one) => one.text)).join(', ')} 는 예정일이 지난 `
        + `건 전체의 범위와 다르다(${input.overdue.month ? `${input.overdue.month} ` : ''}예정일 경과 전체: `
        + `${shortDay(input.overdue.first)}~${shortDay(input.overdue.last)}). 묶음의 범위면 그 묶음을 밝히고, 전체를 말하면 전체 범위로 고쳐라.` : '',
      countWrong.length ? `직전 문서의 현장·담당 건수 ${unique(countWrong.map((one) => one.text)).join(', ')} 는 근거와 다르다(`
        + `${siteTops(input.sites, countWrong)}). 근거의 수로 고치고, 순위는 위에서부터 빠뜨리지 말며, 전체 점검 건수를 미실시로 쓰지 마라.` : '',
      upcoming.length ? `직전 문서의 ${unique(upcoming.map((one) => one.text)).join(', ')} 는 예정일이 아직 안 온 건이 섞여 문제의 크기를 `
        + `보여 주지 못한다(${siteTops(input.sites, upcoming)}). 그 순위는 「예정 포함」을 밝히거나 예정일이 지난 건의 순위로 고쳐라.` : '',
      sendWrong.length ? `직전 문서의 미전송 수 ${unique(sendWrong.map((one) => one.text)).join(', ')} 는 아직 안 한 점검까지 센 수다(근거: `
        + `${sends.map((one) => `${one.month ? `${one.month} ` : ''}미전송 ${one.total.toLocaleString('en-US')}건 중 완료 후 미전송 `
          + `${one.after.toLocaleString('en-US')}건`).join(', ')}). 끝낸 건 중 미전송으로 쓰거나, 나머지는 아직 안 한 점검이라 전송할 것이 `
        + '없다고 밝혀라.' : '',
      together.length ? `직전 문서의 ${unique(together.map((one) => `「${one.text}」`)).join(', ')} 문장은 미전송과 「전송중」을 한 조치로 `
        + '묶었다. 미전송은 전송 처리, 「전송중」은 정보센터 반영 확인이다. 문장을 나누고, 「전송중」은 근거의 담당별 수와 함께 써라.' : '',
      undue.length ? `직전 문서의 ${unique(undue.map((one) => `「${one.text}」`)).join(', ')}는 같은 글의 미실시 수와 맞부딪힌다. 미실시는 `
        + '결과가 계획으로 남은 건 전체다. 예정일이 남은 건은 「예정일이 남은 N건(당일 M건 포함)은 지연으로 보지 않았다」처럼 써라.' : '',
      within.wrong.length ? `직전 문서의 ${unique(within.wrong.map((one) => `「${one.text}」`)).join(', ')}는 수가 맞지 않는다. 예정일이 남은 `
        + `건은 당일 ${within.today.toLocaleString('en-US')}건을 넣으면 ${within.total.toLocaleString('en-US')}건, 빼면 `
        + `${within.later.toLocaleString('en-US')}건이다. 「당일 포함」이면 ${within.total.toLocaleString('en-US')}건으로 고쳐라.` : '',
      mislabeled.length ? `직전 문서의 ${unique(mislabeled.map((one) => `「${one.text}」`)).join(', ')}는 아직 안 한 점검 중 정보센터 미전송으로 `
        + '찍힌 수다. 미실시(결과가 계획으로 남은 건 전체)와 수가 다르니 「미전송 N건 중 나머지 M건은 아직 실시하지 않아 전송할 것이 없다」처럼 써라.' : '',
    ].filter(Boolean);
    const bad = new Set([
      ...(stray.length ? sentencesOf(doc).filter((one) => unsupportedNumbers(one, known).length) : []),
      ...[...mixed, ...memoWrong, ...sendWrong, ...siteWrong, ...exampleWrong, ...rangeWrong, ...together, ...undue,
        ...within.wrong, ...mislabeled].map((one) => one.sentence),
    ]);
    return { asks, bad };
  };
  // 현장 이름은 원자료 그대로(17차 검토: 「(가나)다라기술원」을 「가나 다라기술원」으로 썼다).
  // 절 제목은 정해진 이름만 남긴다. 지시문의 설명을 제목째 옮겨 「## 요약, 핵심 세 줄 안팎」이 나갔다(실측 2026-10-27 18:46).
  const finish = (doc: string) => withFixed(withSiteNames(sectionTitles(doc), input.sites?.names), fixed);
  const first = tidyAnswer((await input.chat(prompt, { step: 'answer' })).trim());
  const found = review(first);
  if (!found.asks.length) return finish(first);
  console.debug('[agent] 작성 다시 씀', found.asks.join(' / ').slice(0, 400));
  const again = tidyAnswer((await input.chat(`${prompt}

직전 문서:
${first}

${found.asks.join('\n')}
문서 전체를 다시 써라.`,
  { step: 'answer' })).trim());
  // 한 번 다시 써도 틀린 문장은 뺀다(18차 검토). 다 빠지면 그대로 둔다(실패해도 뭐라도 답한다).
  const draft = again || first;
  const left = again ? review(again) : found;
  console.debug('[agent] 작성 재검사', left.bad.size ? `남은 결함 문장 ${left.bad.size}개 뺌: ${Array.from(left.bad).join(' / ').slice(0, 300)}` : '통과');
  const kept = withoutSentences(draft, left.bad);
  return finish(kept || draft);
}

/** 작성 담당 문서의 절 이름(`WRITER_PROMPT`). */
const SECTIONS = ['요약', '갈래별로 본 것', '눈여겨볼 점', '기준과 한계'];

/** 절 제목 뒤에 대시, 쉼표, 쌍점으로 붙은 지시문 설명(「, 핵심 세 줄 안팎」)을 뗀다. */
export function sectionTitles(doc: string): string {
  return doc.replace(/^(#{2,3})\s*([^\n]+)$/gm, (line, marks: string, title: string) => {
    const name = SECTIONS.find((one) => title.startsWith(one));
    // 대시나 쉼표로 붙은 설명만 뗀다. 「요약 및 결론」, 「기준과 한계: 자료 범위」 같은 제목은 둔다(독립 재검토).
    return name && /^\s*[—,]/.test(title.slice(name.length)) ? `${marks} ${name}` : line;
  });
}

/** 메모 예시에서 대조할 장치 낱말: [문서 쪽 낱말, 그 현장 메모에 있어야 할 낱말(같은 뜻 줄임말 포함)]. */
const DEVICES: [RegExp, RegExp][] = [
  [/배터리|밧데리/, /배터리|밧데리/],
  [/구출/, /구출|ARD|ELD/i],
  [/비상\s*통화|통화\s*장치|인터폰/, /비상\s*통화|비통|통화|인터폰|국선|유심/],
  [/비상등/, /비상등/],
  [/멀티\s*빔|센서/, /멀티\s*빔|센서/],
  [/모터/, /모터/],
  [/로프/, /로프/],
  [/보드/, /보드/],
  [/침수|누수/, /침수|누수/],
];

/** 현장 이름 꼴: 띄어쓰기와 괄호를 가리지 않는다. */
function namePattern(name: string, flags = 'g'): RegExp {
  const chars = name.replace(/[()\s]/g, '').split('').map((ch) => ch.replace(/[.*+?^${}|[\]\\]/g, '\\$&'));
  return new RegExp(`${name.startsWith('(') ? '\\(?' : ''}${chars.join('[\\s()]*')}${name.endsWith(')') ? '\\)?' : ''}`, flags);
}

/**
 * 메모 문장에서 현장과 함께 쓴 장치 낱말이 그 현장 메모에 없는 것(17차 검토: 「가나대학교 사범대의 구출배터리 교체 필요」는
 * 가나대 다라관 메모였다). 「비상통화」는 메모의 「비통」, 「구출」은 「ARD」로도 받는다.
 */
function memoExampleMismatch(doc: string, memos: { site: string; memo: string }[] | undefined): { site: string; claim: string; memo: string; sentence: string }[] {
  if (!memos?.length) return [];
  const bySite = new Map<string, string>();
  memos.forEach((one) => bySite.set(one.site, `${bySite.get(one.site) ?? ''} ${one.memo}`.trim()));
  const names = Array.from(bySite.keys()).filter((name) => name.replace(/[()\s]/g, '').length >= 3)
    .sort((left, right) => right.length - left.length);
  const wrong: { site: string; claim: string; memo: string; sentence: string }[] = [];
  sentencesOf(doc).forEach((sentence) => {
    if (!/메모/.test(sentence)) return;
    const claimed: { name: string; start: number; end: number }[] = [];
    names.forEach((name) => {
      const pattern = namePattern(name);
      for (let hit = pattern.exec(sentence); hit; hit = pattern.exec(sentence)) {
        const start = hit.index;
        const end = start + hit[0].length;
        if (!claimed.some((one) => start < one.end && end > one.start)) claimed.push({ name, start, end });
      }
    });
    claimed.sort((left, right) => left.start - right.start).forEach((one, at) => {
      const clause = sentence.slice(one.end, claimed[at + 1]?.start ?? sentence.length)
        .replace(/^\s*(?:의|에는|에서는|에서|은|는|에)\s*/, '').replace(/[,\s]+$/, '').trim();
      const memo = bySite.get(one.name) ?? '';
      if (DEVICES.some(([said, kept]) => said.test(clause) && !kept.test(memo))) {
        wrong.push({ site: one.name, claim: clause.slice(0, 40), memo: memo.slice(0, 80), sentence });
      }
    });
  });
  return wrong;
}

/** YYYYMMDD → 「'26.10.1.」. */
function shortDay(day: number): string {
  return `'${String(Math.floor(day / 10000)).slice(2)}.${Math.floor(day / 100) % 100}.${day % 100}.`;
}

/**
 * 「예정일이 지난 … 예정일은 X~Y」에서 X~Y가 경과 전체의 범위와 다른 것(17차 검토: 「10월만 경과」 묶음의 범위를 전체로 썼다).
 * 묶음을 밝힌 문장(「…만 예정일」, 「모두 미실시」, 「N호기」, 「예정분」)은 보지 않는다.
 */
function rangeMismatch(doc: string, overdue: { month: string; first: number; last: number } | undefined): Flagged[] {
  if (!overdue) return [];
  const year = Math.floor(overdue.first / 10000);
  const wrong: Flagged[] = [];
  sentencesOf(doc).forEach((sentence) => {
    if (!/예정일이?\s*지난|예정일\s*경과/.test(sentence) || /만\s*예정일|모두\s*미실시|묶음|\d+\s*호기|예정분/.test(sentence)) return;
    const at = sentence.search(/예정일은/);
    if (at < 0) return;
    const rest = sentence.slice(at);
    const dotted = rest.match(/'(\d{2})\.\s*(\d{1,2})\.\s*(\d{1,2})\.\s*~\s*'(\d{2})\.\s*(\d{1,2})\.\s*(\d{1,2})\./);
    const korean = rest.match(/(\d{1,2})\s*월\s*(\d{1,2})\s*일?\s*[~～-]\s*(?:(\d{1,2})\s*월\s*)?(\d{1,2})\s*일/);
    const day = (y: number, m: number, d: number) => y * 10000 + m * 100 + d;
    const span = dotted
      ? [day(2000 + Number(dotted[1]), Number(dotted[2]), Number(dotted[3])), day(2000 + Number(dotted[4]), Number(dotted[5]), Number(dotted[6])), dotted[0]] as const
      : korean
        ? [day(year, Number(korean[1]), Number(korean[2])), day(year, Number(korean[3] ?? korean[1]), Number(korean[4])), korean[0]] as const
        : undefined;
    if (span && (span[0] !== overdue.first || span[1] !== overdue.last)) wrong.push({ text: span[2], sentence });
  });
  return wrong;
}

/** 현장 이름을 원자료 그대로: 띄어쓰기·괄호만 다른 꼴(「가나 다라기술원」)을 원래 이름으로 고친다. 짧은 이름은 둔다. */
function withSiteNames(doc: string, names: string[] | undefined): string {
  if (!names?.length) return doc;
  const flat = (value: string) => value.replace(/\s+/g, '');
  // 맞춘 이름은 자리표로 막아 둔다. 긴 이름을 맞춘 뒤 짧은 이름(「가나화학 다라공장(PVC)」)의 꼴이 그 안을 다시 잡아
  // 「가나화학 다라공장(PVC) SL-3)」으로 바꿨다(실측 06:37).
  const placed: string[] = [];
  const marked = names.filter((name) => /[()\s]/.test(name) && name.replace(/[()\s]/g, '').length >= 4)
    .sort((left, right) => right.length - left.length)
    .reduce((text, name) => text.replace(namePattern(name), (hit) => {
      placed.push(flat(hit) === flat(name) ? hit : name);
      return `\u0000${placed.length - 1}\u0000`;
    }), doc);
  return marked.replace(/\u0000(\d+)\u0000/g, (_, at: string) => placed[Number(at)] ?? '');
}

/** 코드가 쓴 절을 알리는 규칙. 묶음 이름의 보기는 첫 줄에서 딴다(「9·10월 모두 미실시 38호기」). */
function fixedRule(fixed: { title: string; lines: string[] }): string {
  const name = fixed.lines[0]?.split(/[(:]/)[0]?.trim() ?? '';
  return `- 「${fixed.title}」 절은 우리가 요약 바로 아래에 아래 줄 그대로 싣는다. 너는 그 절을 쓰지 않는다. 그 묶음의 호기 수, `
    + `예정일, 담당 구성을 다른 절에서 다시 풀어 쓰지 말고, 묶음을 말할 때는 묶음 이름과 호기 수만 쓴다${name ? `(「${name}」)` : ''}. `
    + `한 묶음의 담당이나 날짜를 다른 묶음이나 달 합계에 옮겨 쓰지 않는다.\n${fixed.lines.map((line) => `  - ${line}`).join('\n')}\n`;
}

/**
 * 코드가 쓴 절을 요약 절 바로 뒤에 넣는다. 모델이 같은 제목의 절을 썼으면 그것은 빼고, 코드가 쓴 줄을 다른 곳에 옮겨
 * 적었으면 그 줄도 뺀다(실측 14:47: 묶음 줄이 요약 안과 바로 아래 절에 두 번 나왔다).
 */
function withFixed(doc: string, fixed?: { title: string; lines: string[] }): string {
  if (!fixed) return doc;
  const plain = (line: string) => line.replace(/^\s*[-*]\s+/, '').replace(/\s+$/, '');
  const written = new Set(fixed.lines.map(plain));
  const lines = doc.split('\n').filter((line) => !written.has(plain(line)))
    .filter((line, at, all) => line.trim() !== '' || at === 0 || (all[at - 1] ?? '').trim() !== '');
  const own = lines.findIndex((line) => line.trim() === `## ${fixed.title}`);
  if (own >= 0) {
    const end = lines.findIndex((line, at) => at > own && /^##\s/.test(line));
    lines.splice(own, (end < 0 ? lines.length : end) - own);
  }
  const summary = lines.findIndex((line) => /^##\s*요약/.test(line));
  const next = summary >= 0 ? lines.findIndex((line, at) => at > summary && /^##\s/.test(line)) : -1;
  const at = summary < 0 ? 0 : next < 0 ? lines.length : next;
  lines.splice(at, 0, `## ${fixed.title}`, '', ...fixed.lines.map((line) => `- ${line}`), '');
  return lines.join('\n');
}

/**
 * 우리 쪽 작업을 말한 문장. 대조는 코드가 하고 맞지 않는 숫자는 뺀다. 갈래끼리 건수가 다르다는 말과
 * 「제시하지 않았습니다」도 우리 쪽 사정이다(실측 12:46).
 */
const PROCESS_TALK = /대조하지\s*않았|합산하거나|집계\s*기준(?:에|의)?\s*차이|(?:건수|합계)[^.]{0,30}(?:와|과)\s*(?:달라|다르|일치하지\s*않|맞지\s*않)|제시하지\s*않|(?:으로|로)\s*집계했습니다|검산이\s*맞지\s*않/;

/** 앞 문장에 기대는 이음말. 앞 문장을 뺐으면 뗀다(14차 검토: 「… 집계했습니다. 따라서 …」). */
const LEANING = /^(?:따라서|그래서|그러므로|이에\s*따라|이\s*때문에)\s*,?\s*/;

/**
 * 1턴 답에서 우리 쪽 작업을 말한 문장(「갈래별 수치를 합산하거나 직접 대조하지 않았습니다」, 「집계 기준에 차이가
 * 있습니다」)을 뺀다(11차 검토). em-dash와 「 · 」 나열은 쉼표로 바꾼다(사용자 전역 지침 stop-slop-ko).
 * 그런 문장만 있던 줄은 줄째 뺀다.
 */
export function tidyAnswer(doc: string): string {
  return doc.split('\n').flatMap((raw) => {
    // 기간 문장에 붙은 계산용 과제 정리(「…이며, 구분은 현장, 처리는 미실시 점검 확인입니다」)는 뗀다(실측 14:47, 11:56).
    // 「9·10월 모두 미실시 38호기(두 달 76건)이며」는 주어가 없다(16차 검토): 「…미실시인 호기는 38호기(…)이며」.
    const line = raw.replace(/(기간은\s*[^,]+?)이며,\s*구분은\s*[^,.]+?(?:,\s*처리는\s*[^,.]+?)?입니다/, '$1입니다')
      .replace(/(\d{1,2}·\d{1,2}월 모두 미실시)\s+(\d+\s*호기\([^)]*\))이며/, '$1인 호기는 $2이며');
    const sentences = line.split(/(?<=[다요]\.)\s+/);
    const kept: string[] = [];
    let dropped = false;
    let carry = '';
    sentences.forEach((one) => {
      // 「다음 근무일은 '26.10.26.입니다」, 「10월 말일은 10월 31일입니다」만 있는 문장은 근거를 옮긴 홑문장이다(16차 검토, 실측 16:16).
      // 목록 줄의 첫 문장이었으면 목록 표시를 다음 문장에 넘긴다.
      const marker = one.match(/^\s*([-*]\s+)/)?.[1] ?? '';
      if (PROCESS_TALK.test(one) || /^(?:[-*]\s+)?(?:다음\s*근무일은|(?:이번\s*달|\d{1,2}\s*월)\s*말일은)\s*.+?입니다\.?$/.test(one.trim())) {
        dropped = true;
        carry = carry || marker;
        return;
      }
      const trimmed = dropped ? one.replace(LEANING, '') : one;
      kept.push(carry && !/^\s*[-*]\s+/.test(trimmed) ? `${carry}${trimmed}` : trimmed);
      carry = '';
      dropped = false;
    });
    if (kept.length === sentences.length) return [line];
    return kept.length ? [kept.join(' ')] : [];
  }).join('\n').replace(/\s*—\s*/g, ', ').replace(/ · /g, ', ')
    // 「필수 여부 확인 필요합니다」는 「확인이 필요합니다」로(14차 검토).
    .replace(/확인\s*필요합니다/g, '확인이 필요합니다')
    // 「전송중」 표기는 보고서와 같은 낫표로(20차 검토: 1턴은 ‘전송중’, 보고서는 「전송중」이었다).
    .replace(/[‘'"“]전송중[’'"”]/g, '「전송중」');
}

/** 검사가 찾은 결함과 그 문장. 다시 써도 남으면 그 문장을 뺀다(18차 검토). */
type Flagged = { text: string; sentence: string };

/** 문서를 줄과 문장으로 나눈 조각. 검사와 문장 빼기가 같은 조각을 쓴다. */
function sentencesOf(doc: string): string[] {
  return doc.split('\n').flatMap((line) => line.split(/(?<=[다요]\.)\s+/));
}

function unique(list: string[]): string[] {
  return Array.from(new Set(list));
}

/**
 * 다시 써도 남은 결함 문장을 뺀다(18차 검토: 한 번 다시 써도 틀리면 그 문장을 빼는 쪽이 안전하다). 제목 줄은 두고, 목록 줄의 첫
 * 문장이면 목록 표시를 다음 문장에 넘긴다. 뺀 문장 다음의 연결어(「따라서」)도 뗀다. 비게 된 줄과, 그 때문에 내용이 없어진 제목은 뺀다.
 */
function withoutSentences(doc: string, bad: Set<string>): string {
  if (!bad.size) return doc;
  const original = doc.split('\n');
  const level = (line: string) => line.match(/^\s*(#+)\s/)?.[1]?.length ?? 0;
  const emptied = new Set<number>();
  const lines = original.map((line, index) => {
    if (level(line)) return line;
    const pieces = line.split(/(?<=[다요]\.)\s+/);
    if (!pieces.some((one) => bad.has(one))) return line;
    const marker = line.match(/^\s*(?:[-*]|\d+[.)])\s+/)?.[0] ?? '';
    const kept: string[] = [];
    let dropped = false;
    pieces.forEach((one) => {
      if (bad.has(one)) {
        dropped = true;
        return;
      }
      const piece = dropped ? one.replace(LEANING, '') : one;
      kept.push(kept.length || !marker || piece.startsWith(marker) ? piece : `${marker}${piece}`);
      dropped = false;
    });
    if (!kept.length) emptied.add(index);
    return kept.join(' ');
  });
  // 내용 줄이 모두 빠진 제목. 아래 제목(###)에 남은 내용이 있으면 위 제목(##)은 둔다.
  const gone = new Set<number>();
  original.forEach((line, index) => {
    const own = level(line);
    if (!own) return;
    let end = index + 1;
    while (end < original.length && !(level(original[end] ?? '') && level(original[end] ?? '') <= own)) end += 1;
    const content = Array.from({ length: end - index - 1 }, (_, at) => index + 1 + at)
      .filter((at) => !level(original[at] ?? '') && (original[at] ?? '').trim());
    if (content.length && content.every((at) => emptied.has(at))) gone.add(index);
  });
  return lines.filter((_, index) => !emptied.has(index) && !gone.has(index)).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

type MonthCounts = { month: string; all: [string, number][]; late: [string, number][] }[];
type SiteCounts = { names: string[]; months: MonthCounts; owners?: { names: string[]; months: MonthCounts } };
type SiteWrong = Flagged & { month: string; late: boolean; owner: boolean; kind: 'count' | 'missing' | 'upcoming' };

/** 순위 문장: 「순입니다」, 「많은」, 「가장」. 「단순」, 「순차」는 아니다. */
const RANKED = /(?:^|[\s,])순(?:위|서|으로|이었|이다|입니다|이며|이고|임)|많|가장|상위|최다/;
/** 진행 중인 달의 미실시 순위에 예정일이 아직 안 온 건이 섞였다고 밝힌 말(「예정 포함」, 「예정일 이내」). */
const UPCOMING_SAID = /예정\S{0,2}\s*(?:건\s*)?(?:까지\s*)?포함|예정일\s*(?:이내|전)|예정일이\s*(?:아직\s*)?(?:오지|안\s*온|남은|지나지)|아직\s*예정일/;

/**
 * 미실시·경과 문장의 「현장 N건」·「담당 N건」 중 그 달 수와 다른 것(count), 순위 문장이 적은 가장 작은 수보다 많은 이름을 뺀
 * 것(missing), 미실시 순위에 예정일이 아직 안 온 건이 섞였는데 밝히지 않은 것(upcoming). 실측 15:20은 현장별 전체 점검 건수를
 * 미실시로 썼고, 16:16은 「F 38건, E 38건, D 36건이 많습니다」로 A 49건·B 41건을 뺐으며, 그 순위는
 * 예정일이 안 온 건까지 센 것이라 문제의 크기를 보여 주지 못했다(18차 검토).
 */
function siteMismatch(doc: string, sites: SiteCounts | undefined): SiteWrong[] {
  if (!sites?.months.length) return [];
  return rankCheck(doc, [
    { names: sites.names, months: sites.months, owner: false },
    ...(sites.owners ? [{ names: sites.owners.names, months: sites.owners.months, owner: true }] : []),
  ]);
}

/**
 * 이름별 수 대조. 현장과 담당 이름을 함께 찾아 긴 이름부터 차지한다(현장 이름 속 사람 이름을 담당으로 세지 않는다). 현장 이름은
 * 띄어쓰기와 괄호를 가리지 않고, 괄호 안 짧은 이름(「동역」)은 한 현장에만 있을 때 받는다. 「서중앙역」 안의 「중앙역」은 세지
 * 않는다. 「A와 B 각 40건」은 둘 다 40건이다. 달은 그 자리 앞의 가장 가까운 달(「이번 달」은 기준월, 「지난달」은 그 전 달), 없으면
 * 같은 문단의 앞 문장 달이다. 예정일이 지났다고 한 문장은 경과 수와 견준다.
 */
function rankCheck(doc: string, groups: { names: string[]; months: MonthCounts; owner: boolean }[]): SiteWrong[] {
  const strip = (value: string) => value.replace(/[()]/g, '');
  const spaced = (form: string) => strip(form).replace(/\s+/g, '').split('').map((ch) => ch.replace(/[.*+?^${}|[\]\\]/g, '\\$&')).join('\\s*');
  const forms = groups.flatMap((group, at) => {
    const shortOf = (name: string) => (group.owner ? '' : name.match(/\(([^()]+)\)\s*$/)?.[1]?.trim() ?? '');
    const shorts = new Map<string, number>();
    group.names.forEach((name) => { const one = shortOf(name); if (one) shorts.set(one, (shorts.get(one) ?? 0) + 1); });
    return group.names.flatMap((name) => {
      const short = shortOf(name);
      return [{ name, at, size: strip(name).replace(/\s+/g, '').length, pattern: new RegExp(`${group.owner ? '(?<![가-힣])' : ''}${spaced(name)}`, 'g') },
        ...(short.length >= 2 && shorts.get(short) === 1
          ? [{ name, at, size: short.replace(/\s+/g, '').length, pattern: new RegExp(`(?<![가-힣A-Za-z0-9])${spaced(short)}`, 'g') }] : [])];
    });
  }).sort((left, right) => right.size - left.size);
  if (!forms.length) return [];
  const tables = groups.map((group) => new Map(group.months.map((one) => [one.month, { all: new Map(one.all), late: new Map(one.late) }])));
  const keys = groups[0]?.months.map((one) => one.month) ?? [];
  const latest = keys[keys.length - 1] ?? '';
  const previous = keys[keys.length - 2] ?? '';
  const lastMonth = (upto: string) => {
    const found = Array.from(upto.matchAll(/(\d{1,2})\s*월|이번\s*달|이달|금월|지난\s*달|전월/g)).pop();
    if (!found) return '';
    if (found[1]) return `${Number(found[1])}월`;
    return /지난|전월/.test(found[0]) ? previous : latest;
  };
  const number = (value: number) => value.toLocaleString('en-US');
  const measure = (late: boolean) => (late ? '예정일 경과' : '미실시');
  const wrong: SiteWrong[] = [];
  doc.split('\n').forEach((paragraph) => {
    let carried = '';
    paragraph.split(/(?<=[다요]\.)\s+/).forEach((raw) => {
      const sentence = strip(raw);
      const checked = /미실시|예정일|경과|계획/.test(sentence) && !/메모|미전송|전송|서명/.test(sentence);
      if (checked) {
        const late = /예정일이?\s*지난|예정일\s*경과|경과/.test(sentence);
        const claimed: { name: string; at: number; start: number; end: number }[] = [];
        forms.forEach((form) => {
          form.pattern.lastIndex = 0;
          for (let hit = form.pattern.exec(sentence); hit; hit = form.pattern.exec(sentence)) {
            const start = hit.index;
            const end = start + hit[0].length;
            if (!claimed.some((one) => start < one.end && end > one.start)) claimed.push({ name: form.name, at: form.at, start, end });
          }
        });
        claimed.sort((left, right) => left.start - right.start);
        let pending: typeof claimed = [];
        const listed = new Map<string, { at: number; month: string; entries: { name: string; value: number }[] }>();
        claimed.forEach((one, index) => {
          const after = sentence.slice(one.end, claimed[index + 1]?.start ?? sentence.length);
          // 「B가 각 21건」은 조사와 「각」 사이가 띄어져 있다(실측 16:16).
          const count = after.match(/^\s*[가-힣]{0,3}\s*(?:각\s*)?(\d[\d,]*)\s*건/);
          if (!count) {
            pending = /^\s*(?:와|과|,|·|및|그리고)\s*$/.test(after) ? [...pending, one] : [];
            return;
          }
          const value = Number((count[1] ?? '').replace(/,/g, ''));
          const group = /각/.test(after) ? [...pending, one] : [one];
          pending = [];
          group.forEach((member) => {
            const month = lastMonth(sentence.slice(0, member.start)) || carried;
            const counts = tables[member.at]?.get(month);
            if (!counts) return;
            const key = `${member.at}|${month}`;
            const entry = listed.get(key) ?? { at: member.at, month, entries: [] };
            entry.entries.push({ name: member.name, value });
            listed.set(key, entry);
            const expected = (late ? counts.late : counts.all).get(member.name) ?? 0;
            if (expected !== value) {
              wrong.push({
                text: `${member.name} ${number(value)}(${month} ${measure(late)} ${number(expected)})`,
                month, late, owner: !!groups[member.at]?.owner, kind: 'count', sentence: raw,
              });
            }
          });
        });
        if (RANKED.test(sentence)) {
          listed.forEach(({ at, month, entries }) => {
            const counts = tables[at]?.get(month);
            if (!counts) return;
            const owner = !!groups[at]?.owner;
            // 순위 문장은 적은 가장 작은 수보다 많은 이름을 빼지 않는다.
            if (entries.length >= 2) {
              const floor = Math.min(...entries.map((one) => one.value));
              const missing = Array.from((late ? counts.late : counts.all).entries())
                .filter(([name, count]) => count > floor && !entries.some((one) => one.name === name));
              if (missing.length) {
                wrong.push({
                  text: `순위에서 빠진 이름 ${missing.map(([name, count]) => `${name} ${number(count)}`).join(', ')}(${month} ${measure(late)})`,
                  month, late, owner, kind: 'missing', sentence: raw,
                });
              }
            }
            // 미실시 순위에 예정일이 아직 안 온 건이 섞였으면 「예정 포함」을 밝힌다(18차 검토).
            if (!late && !UPCOMING_SAID.test(sentence)
              && entries.some((one) => (counts.all.get(one.name) ?? 0) > (counts.late.get(one.name) ?? 0))) {
              wrong.push({ text: `예정일이 지나지 않은 건까지 센 순위(${month} 미실시)`, month, late, owner, kind: 'upcoming', sentence: raw });
            }
          });
        }
      }
      carried = lastMonth(sentence) || carried;
    });
  });
  return wrong;
}

/** 고치게 할 때 보여 줄 그 달 현장별·담당별 수(여덟까지). 예정 포함 순위에는 예정일 경과 순위를 보여 준다. */
function siteTops(sites: SiteCounts | undefined, wrong: SiteWrong[]): string {
  const seen = new Set<string>();
  return wrong.map((one) => ({ ...one, late: one.late || one.kind === 'upcoming' }))
    .filter((one) => { const key = `${one.month}${one.late}${one.owner}`; if (seen.has(key)) return false; seen.add(key); return true; })
    .map((one) => {
      const source = one.owner ? sites?.owners?.months : sites?.months;
      const list = source?.find((month) => month.month === one.month)?.[one.late ? 'late' : 'all'] ?? [];
      return `${one.month} ${one.late ? '예정일 경과' : '미실시'} ${one.owner ? '담당별' : '현장별'}: `
        + `${list.slice(0, 8).map(([name, count]) => `${name} ${count.toLocaleString('en-US')}`).join(', ')}`;
    }).join(' / ');
}

/** 근거의 달별 미전송(「10월 정보센터 미전송 328건 중 완료 후 미전송 2건」). */
function sendCounts(facts: string[]): { month: string; total: number; after: number }[] {
  return facts.flatMap((line) => {
    const found = line.match(/^(?:(\d{1,2})월 )?정보센터 미전송 (\d[\d,]*)건 중 완료 후 미전송 (\d[\d,]*)건/);
    if (!found) return [];
    const total = Number((found[2] ?? '').replace(/,/g, ''));
    const after = Number((found[3] ?? '').replace(/,/g, ''));
    return total > after ? [{ month: found[1] ? `${Number(found[1])}월` : '', total, after }] : [];
  });
}

/** 미전송 문장에서 그 달 미전송 전체 수를 완료 건 말(완료 후, 아직 안 한, 나머지) 없이 쓴 수. */
function sendMismatch(doc: string, sends: { month: string; total: number; after: number }[]): Flagged[] {
  if (!sends.length) return [];
  const totals = new Set(sends.map((one) => one.total));
  const wrong: Flagged[] = [];
  sentencesOf(doc).forEach((sentence) => {
    if (!/미전송/.test(sentence) || /완료\s*후|아직\s*안\s*한|나머지|전송할\s*것이\s*없/.test(sentence)) return;
    (sentence.match(/\d[\d,]*(?=\s*건)/g) ?? []).forEach((token) => {
      const value = Number(token.replace(/,/g, ''));
      if (totals.has(value)) wrong.push({ text: value.toLocaleString('en-US'), sentence });
    });
  });
  return wrong;
}

/**
 * 미전송과 「전송중」을 한 조치로 묶은 문장(19차 검토: 「미전송 3건과 전송 상태가 바뀌지 않은 4건 … 반영 여부를 확인해야」).
 * 미전송은 전송 처리, 「전송중」은 반영 확인이다. 미전송 건수(0이 아닌 수)가 있는 문장만 본다. 전송 처리를 따로 말했거나
 * 둘을 갈라 말한 문장은 걸지 않는다(실측 06:37: 「미전송이 없지만 '전송중' 9건은 별도로 …」를 잡아 뺐다).
 */
function sendTogether(doc: string): Flagged[] {
  return sentencesOf(doc)
    .filter((sentence) => /미전송\s*(?:은|는|이|가|도)?\s*[1-9]/.test(sentence)
      && /전송중|전송\s*상태가\s*바뀌지|바뀌지\s*않은/.test(sentence)
      && !/전송\s*처리|전송을|전송해|보내|별도|따로|다르/.test(sentence))
    .map((sentence) => ({ text: sentence.trim().slice(0, 60), sentence }));
}

/**
 * 「예정일이 남은 N건(당일 M건 포함)」의 N이 당일을 넣은 수(경과 외 전체)인가(실측 07:33: 「232건(당일 112건 포함)」, 당일을 넣으면
 * 344건이다). 근거의 「경과 외 미실시 T건(당일 M건 + 이후 U건)」으로 본다. 돌려주는 값은 [틀린 문장들, 근거의 T·M·U].
 */
function withinMismatch(doc: string, facts: string[]): { wrong: Flagged[]; total: number; today: number; later: number } {
  const found = facts.map((line) => line.match(/경과 외 미실시 (\d[\d,]*)건\(당일 (\d[\d,]*)건 \+ 이후 (\d[\d,]*)건\)/)).find(Boolean);
  const value = (text: string | undefined) => Number((text ?? '').replace(/,/g, ''));
  if (!found) return { wrong: [], total: 0, today: 0, later: 0 };
  const [total, today, later] = [value(found[1]), value(found[2]), value(found[3])];
  const wrong = sentencesOf(doc).flatMap((sentence) => {
    const hit = sentence.match(/(\d[\d,]*)\s*건\s*\(\s*당일(?:\s*예정)?\s*(\d[\d,]*)\s*건\s*포함\s*\)/);
    return hit && value(hit[2]) === today && value(hit[1]) !== total ? [{ text: hit[0], sentence }] : [];
  });
  return { wrong, total, today, later };
}

/**
 * 아직 안 한 점검 중 정보센터 미전송으로 찍힌 나머지 수를 「미실시 N건」으로 쓴 문장(실측 07:33: 「미실시 11건은 아직 실시하지 않아 …」,
 * 9월 미실시는 51건). 그 달 미실시 전체와 같은 수는 맞는 말이라 걸지 않는다.
 */
function remainderMislabel(doc: string, sends: { month: string; total: number; after: number }[], facts: string[]): Flagged[] {
  const totals = new Set(facts.flatMap((line) => Array.from(line.matchAll(/미실시 (\d[\d,]*)건\(\d/g))
    .map((match) => Number((match[1] ?? '').replace(/,/g, '')))));
  const rests = new Set(sends.map((one) => one.total - one.after).filter((left) => left > 0 && !totals.has(left)));
  if (!rests.size) return [];
  return sentencesOf(doc).flatMap((sentence) => {
    if (!/전송/.test(sentence)) return [];
    const hit = Array.from(sentence.matchAll(/미실시\s*(\d[\d,]*)\s*건/g)).find((match) => rests.has(Number((match[1] ?? '').replace(/,/g, ''))));
    return hit ? [{ text: hit[0], sentence }] : [];
  });
}

/** 「미실시로 확정하지 않았다」(19차 검토). 미실시는 계획으로 남은 건 전체라 같은 글의 미실시 수와 맞부딪힌다. */
function undueWrong(doc: string): Flagged[] {
  return sentencesOf(doc)
    .filter((sentence) => /미실시로\s*(?:확정|단정|판정|분류|보|세|치)[가-힣]{0,3}\s*않/.test(sentence))
    .map((sentence) => ({ text: sentence.trim().slice(0, 60), sentence }));
}

/** 근거의 달별 메모 현장 수(「10월 메모 이상 24개 현장」). 달 칸이 없으면 열쇠는 빈 글자다. */
function memoCounts(facts: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  facts.forEach((line) => {
    const found = line.match(/^(?:(\d{1,2})월 )?메모 이상 (\d[\d,]*)개 현장/);
    if (found) counts.set(found[1] ? `${Number(found[1])}월` : '', Number((found[2] ?? '').replace(/,/g, '')));
  });
  return counts;
}

/** 문서의 메모 문장에서 근거와 다른 「N개 현장」. 그 수 앞의 가장 가까운 달로 맞추고, 달이 없으면 근거의 어느 수와도 같아야 한다. */
function memoMismatch(doc: string, counts: Map<string, number>): Flagged[] {
  if (!counts.size) return [];
  const wrong: Flagged[] = [];
  sentencesOf(doc).forEach((sentence) => {
    if (!/메모/.test(sentence)) return;
    const pattern = /(\d[\d,]*)\s*개\s*현장/g;
    for (let match = pattern.exec(sentence); match; match = pattern.exec(sentence)) {
      const month = sentence.slice(0, match.index).match(/(\d{1,2})\s*월(?![\s\S]*\d\s*월)/)?.[1];
      const name = month ? `${Number(month)}월` : '';
      const value = Number((match[1] ?? '').replace(/,/g, ''));
      const expected = counts.get(name);
      const fine = expected !== undefined ? expected === value : Array.from(counts.values()).indexOf(value) >= 0;
      if (!fine) wrong.push({ text: name ? `${value}(${name})` : String(value), sentence });
    }
  });
  return wrong;
}

/** 달 이름 열쇠(202609, 2026-09, 「2026년 9월」, 「9월」)를 「9월」로. 달이 아니면 undefined. */
function monthName(key: string): string | undefined {
  const plain = key.trim();
  const numeric = plain.match(/^(?:19|20)\d{2}[-.]?(0?[1-9]|1[0-2])$/);
  if (numeric) return `${Number(numeric[1])}월`;
  const korean = plain.match(/^(?:(?:19|20)\d{2}\s*년\s*)?(\d{1,2})\s*월$/);
  const month = korean ? Number(korean[1]) : 0;
  return month >= 1 && month <= 12 ? `${month}월` : undefined;
}

/** 달 칸이 있는 행의 달(「월」·「대상월」 칸). */
function rowMonth(node: unknown): [string, string] | undefined {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return undefined;
  const field = Object.entries(node as Record<string, unknown>).find(([key, value]) => /^(?:대상)?월$|^달$|^month$/i.test(key)
    && (typeof value === 'string' || typeof value === 'number') && monthName(String(value)));
  return field ? [field[0], monthName(String(field[1])) as string] : undefined;
}

type MonthLeaf = { path: string; month: string; value: number };

/**
 * 갈래 값에서 달마다 나뉜 수. 달 이름 열쇠 아래, 또는 「월」 칸이 있는 행의 수를 (달을 뺀 길, 달)로 모은다.
 * 순위는 이름으로 맞춘다: 이름·수 짝([이름, 수])과 이름 칸이 있는 행은 달마다 차례가 달라서다. 비율은 더하지 않는다.
 */
function monthLeaves(value: unknown): MonthLeaf[] {
  const found: MonthLeaf[] = [];
  const named = (node: unknown, at: number) => {
    if (Array.isArray(node) && node.length === 2 && typeof node[0] === 'string' && typeof node[1] === 'number') return node[0];
    const name = node && typeof node === 'object' && !Array.isArray(node)
      ? Object.values(node as Record<string, unknown>).find((one) => typeof one === 'string') : undefined;
    return typeof name === 'string' ? name : String(at);
  };
  const leaves = (node: unknown, path: string[], month: string): void => {
    if (typeof node === 'number') {
      if (Number.isFinite(node) && !path.some((key) => /비율|%/.test(key))) found.push({ path: path.join('/'), month, value: node });
    } else if (Array.isArray(node)) {
      const pair = (one: unknown): one is [string, number] => Array.isArray(one) && one.length === 2
        && typeof one[0] === 'string' && typeof one[1] === 'number';
      if (pair(node)) leaves(node[1], [...path, node[0]], month);
      else node.forEach((one, at) => leaves(pair(one) ? one[1] : one, [...path, named(one, at)], month));
    } else if (node && typeof node === 'object') {
      Object.entries(node as Record<string, unknown>).forEach(([key, one]) => leaves(one, [...path, key], month));
    }
  };
  const walk = (node: unknown, path: string[]): void => {
    if (Array.isArray(node)) {
      const months = node.map(rowMonth);
      if (node.length >= 2 && months.every(Boolean)) {
        node.forEach((one, at) => {
          const [field, month] = months[at] as [string, string];
          const rest = { ...(one as Record<string, unknown>) };
          delete rest[field];
          leaves(rest, path, month);
        });
      } else node.forEach((one, at) => walk(one, [...path, String(at)]));
    } else if (node && typeof node === 'object') {
      const entries = Object.entries(node as Record<string, unknown>);
      const monthly = entries.filter(([key]) => monthName(key));
      if (monthly.length >= 2) monthly.forEach(([key, one]) => leaves(one, path, monthName(key) as string));
      entries.filter(([key]) => monthly.length < 2 || !monthName(key)).forEach(([key, one]) => walk(one, [...path, key]));
    }
  };
  walk(value, []);
  return found;
}

/** 그 수를 센 말: 길의 끝 두 마디의 두 글자 묶음. 「상위」「건수」처럼 어디에나 붙는 말과 숫자는 뺀다. */
const PLAIN_PAIR = /^(?:상위|건수|비율|전체|합계|개수|이름|현황|목록|월별|기준|결과|상태)$|\d/;

function pathWords(path: string, axis: string): string[] {
  const tail = path ? path.split('/').slice(-2).join('') : axis;
  const plain = tail.replace(/[\s·,.()%/]/g, '');
  const words = Array.from({ length: Math.max(plain.length - 1, 0) }, (_, index) => plain.slice(index, index + 2));
  return Array.from(new Set(words.filter((word) => !PLAIN_PAIR.test(word))));
}

type Merged = { sum: string; parts: { month: string; value: string }[]; words: string[] };

/**
 * 갈래 값에서 두세 달의 같은 수를 더한 합계. 실측 12:46의 갈래는 달별 값과 함께 두 달 합계(계획 447건 = 9월 51건 +
 * 10월 396건)를 냈다. 한 달 값이나 사실에도 있는 수는 우연히 같을 수 있어 뺀다. 10 미만은 보지 않는다.
 */
function mergedTotals(results: AxisResult[], facts: string[]): Map<number, Merged> {
  const merged = new Map<number, Merged>();
  const known: number[] = [];
  numbersIn(facts, known);
  results.forEach((one) => {
    if (one.failed) return;
    const leaves = monthLeaves(one.value);
    leaves.forEach((leaf) => known.push(leaf.value));
    const byPath = new Map<string, MonthLeaf[]>();
    leaves.forEach((leaf) => byPath.set(leaf.path, [...(byPath.get(leaf.path) ?? []), leaf]));
    byPath.forEach((group) => {
      const months = new Set(group.map((leaf) => leaf.month));
      if (months.size < 2 || months.size > 3 || months.size !== group.length) return;
      const sum = group.reduce((total, leaf) => total + leaf.value, 0);
      if (!Number.isInteger(sum) || sum < 10) return;
      const number = (value: number) => value.toLocaleString('en-US');
      merged.set(sum, {
        sum: number(sum), parts: group.map((leaf) => ({ month: leaf.month, value: number(leaf.value) })),
        words: pathWords(group[0]?.path ?? '', one.axis),
      });
    });
  });
  known.forEach((value) => merged.delete(value));
  return merged;
}

/**
 * 문서에서 두세 달 합계를 쓴 숫자 중 그 줄에 달별 몫(「9월분」)이 없는 것. 날짜는 셈하지 않는다. 그 수를 센 말이 그 줄에
 * 없으면 우연히 같은 수다: 「가나리버파크 21건」(10월 값)이 J의 담당싸인 누락 9월 11건과 10월 10건의 합과 같았다(12:46).
 */
function mixedNumbers(doc: string, merged: Map<number, Merged>): (Flagged & { sum: string; month: string; part: string })[] {
  if (!merged.size) return [];
  const found: (Flagged & { sum: string; month: string; part: string })[] = [];
  doc.split('\n').forEach((line) => {
    if (/\d{1,2}\s*월분/.test(line)) return;
    const flat = line.replace(/\s+/g, '');
    line.split(/(?<=[다요]\.)\s+/).forEach((sentence) => {
      const plain = sentence.replace(/'\d{2}\.\s*\d{1,2}\.\s*\d{1,2}\.|\d{4}\s*년|\d{1,2}\s*월|\d{1,2}\s*일|\d{4}-\d{2}(?:-\d{2})?/g, ' ');
      (plain.match(/\d[\d,]*(?:\.\d+)?/g) ?? []).forEach((token) => {
        const hit = merged.get(Number(token.replace(/,/g, '')));
        const [first] = hit?.parts ?? [];
        if (!hit || !first || !hit.words.some((word) => flat.indexOf(word) >= 0)) return;
        found.push({
          text: `${hit.sum}(${hit.parts.map((part) => `${part.month} ${part.value}`).join(', ')})`,
          sum: hit.sum, month: first.month, part: first.value, sentence,
        });
      });
    });
  });
  return found;
}

function numbersIn(value: unknown, into: number[]): void {
  if (typeof value === 'number' && Number.isFinite(value)) into.push(value);
  else if (typeof value === 'string') {
    const found = value.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/g) ?? [];
    for (let index = 0; index < found.length; index += 1) into.push(Number(found[index]));
  } else if (Array.isArray(value)) value.forEach((one) => numbersIn(one, into));
  else if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    Object.keys(record).forEach((key) => { numbersIn(key, into); numbersIn(record[key], into); });
  }
}

/**
 * 문서 속 숫자 중 갈래 결과에 없는 것. 날짜(2026년·10월·2026-10)와 목록·절 번호는 셈하지 않는다.
 * 반올림은 받아 준다(74.75 → 74.7·74.8·75).
 */
export function unsupportedNumbers(doc: string, results: AxisResult[]): string[] {
  const known: number[] = [];
  results.forEach((one) => numbersIn(one.value, known));
  // 「일」은 일수 단위이기도 하다. 큰 수와 소수의 끝(「1,216일」의 「16일」, 「495.5일」의 「5일」)은 날짜가 아니다(실측 2026-10-26).
  // 달 범위(「11~12월」, 「9·10월」)는 앞 달까지 날짜다. 공지 제목이 「11」 때문에 근거 없음으로 버려졌다(2026-10-26).
  const text = doc
    .replace(/\d{4}\s*년|(?<![\d.,])\d{1,2}\s*[~·]\s*\d{1,2}\s*월|(?<![\d.,])\d{1,2}\s*월|(?<![\d.,])\d{1,2}\s*일|\d{4}-\d{2}(?:-\d{2})?/g, ' ')
    .replace(/^\s*(?:#+\s*)?\d+[.)]\s/gm, ' ');
  const found = text.replace(/(\d),(\d{3})/g, '$1$2').match(/\d+(?:\.\d+)?/g) ?? [];
  const close = (number: number) => known.some((value) => value === number
    || Math.abs(value - number) <= 0.051
    || Math.round(value) === number);
  const stray: string[] = [];
  for (let index = 0; index < found.length; index += 1) {
    const one = found[index] as string;
    if (!close(Number(one)) && stray.indexOf(one) < 0) stray.push(one);
  }
  return stray;
}
